import { useAuth0 } from '@auth0/auth0-react';
import { UserPermissions } from '@/types/menu';

const AUTH0_ROLES_NAMESPACE = import.meta.env.VITE_AUTH0_ROLES_NAMESPACE || 'https://enet.hakusensha.co.jp/roles';

type RoleName = 'admin' | 'uploader' | 'viewer';

const ROLE_PERMISSIONS: Record<RoleName, (keyof UserPermissions)[]> = {
  admin: ['uploadManagement', 'bookDownload', 'memberManagement', 'mbsConversion', 'emailMagazine', 'donationReport'],
  uploader: ['uploadManagement', 'bookDownload'],
  viewer: ['bookDownload', 'donationReport'],
};

/**
 * Auth0 のカスタムクレーム（`user[namespace]`）からロール名の配列を取り出す。
 *
 * `user` は Auth0 SDK 上 `Record<string, unknown>` 相当で、クレームの中身は
 * **テナント側の Action / Rule 次第**なので実行時に確認する必要がある。
 * `as string[]` で黙らせると、文字列以外が混ざったときに `ROLE_PERMISSIONS[role]` が
 * 静かに undefined になり「権限が付かない」形で表面化する（AGENTS.md 1・3）。
 */
const readRoles = (claim: unknown): string[] =>
  Array.isArray(claim) ? claim.filter((role): role is string => typeof role === 'string') : [];

/** ロール名が `ROLE_PERMISSIONS` のキーかを判定する（未知のロールは無視する）。 */
const isKnownRole = (role: string): role is RoleName =>
  Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role);

export const usePermissions = () => {
  const { user } = useAuth0();

  const getUserPermissions = (): UserPermissions => {
    const roles = readRoles(user?.[AUTH0_ROLES_NAMESPACE]);

    const permissions: UserPermissions = {
      uploadManagement: false,
      bookDownload: false,
      memberManagement: false,
      mbsConversion: false,
      emailMagazine: false,
      donationReport: false,
    };

    for (const role of roles) {
      if (!isKnownRole(role)) continue;
      for (const feature of ROLE_PERMISSIONS[role]) {
        permissions[feature] = true;
      }
    }

    // Fallback: if no roles are assigned, grant all permissions (development mode)
    if (roles.length === 0 && import.meta.env.DEV) {
      // キーの集合は UserPermissions の定義そのものなので、上の初期化と同じ形で列挙する
      // （`Object.keys(...) as (keyof UserPermissions)[]` を避ける）。
      permissions.uploadManagement = true;
      permissions.bookDownload = true;
      permissions.memberManagement = true;
      permissions.mbsConversion = true;
      permissions.emailMagazine = true;
      permissions.donationReport = true;
    }

    return permissions;
  };

  const hasPermission = (feature: keyof UserPermissions): boolean => {
    if (!user) return false;

    const permissions = getUserPermissions();
    return permissions[feature] === true;
  };

  const hasAnyPermission = (features: (keyof UserPermissions)[]): boolean => {
    return features.some((feature) => hasPermission(feature));
  };

  const hasAllPermissions = (features: (keyof UserPermissions)[]): boolean => {
    return features.every((feature) => hasPermission(feature));
  };

  return {
    permissions: getUserPermissions(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
